import { gql } from "@apollo/client";
import FormDialog from "controls/FormDialog";
import FiltersBar from "filters/FiltersBar";
import PageSizeFilter from "filters/PageSizeFilter";
import useData from "hooks/useData";
import { Check } from "mdi-material-ui";
import React, { useState } from "react";
import { useParams } from "react-router-dom";

import ReadminObjectsDialogToolbar from "./ReadminObjectsDialogToolbar";
import ReadminObjectsTable, { ReadminObjectsTableFragment } from "./ReadminObjectsTable";
import useReadminObjectTypedef from "./useReadminObjectTypedef";

export default function SelectReadminObjectDialog({
  selectedReadminObjects = [],
  options,
  optionsFilters = {},
  optionsTypename,
  onSubmit,
  onClose,
  ...others
}) {
  if (!optionsTypename) throw new Error("optionsTypename is required");
  const [limit, limitSet] = useState(10);
  const { orgId, apiPartnerId } = useParams();

  // Note: Multi-selectable is false by default, but can be overridden by passing optionsMultiSelectable prop
  const { optionsMultiSelectable: isMultiSelectable = false, ...tableProps } = others;

  const optionsTypedef = useReadminObjectTypedef(optionsTypename, { loadFilterBar: true });

  const [rowsData, rowsDataMeta] = useData(
    gql`
      query SelectReadminObjectDialog(
        $orgId: ID
        $apiPartnerId: ID
        $typename: String!
        $optionsIds: [ID!]
        $filterBar: ReadminFilterBar
        $optionsFilters: [ReadminOptionsFilter!]!
        $limit: Int!
        $offset: Int
      ) {
        readminObjectsCount(
          orgId: $orgId
          apiPartnerId: $apiPartnerId
          typename: $typename
          ids: $optionsIds
          filterBar: $filterBar
          optionsFilters: $optionsFilters
        )
        readminObjects(
          orgId: $orgId
          apiPartnerId: $apiPartnerId
          typename: $typename
          ids: $optionsIds
          filterBar: $filterBar
          optionsFilters: $optionsFilters
          limit: $limit
          offset: $offset
        ) {
          id
          readminName
          ...ReadminObjectsTableFragment
        }
      }
      ${ReadminObjectsTableFragment}
    `,
    {
      orgId,
      apiPartnerId,
      typename: optionsTypename,
      filterBar: optionsTypedef?.filterBarVariables,
      optionsIds: options?.map((option) => option.id),
      limit,
      optionsFilters: Object.entries(optionsFilters).map(([name, value]) => ({
        name,
        value,
      })),
    },
    {
      skip: !optionsTypedef,
    },
  );

  return (
    <FormDialog maxWidth="xl" header="Select..." onClose={onClose} {...tableProps}>
      <ReadminObjectsDialogToolbar
        typename={optionsTypename}
        onSubmit={async (result) => {
          if (result?.id && result?.__typename === optionsTypename) {
            await onSubmit?.(result);
            onClose?.();
            return;
          }
          if (rowsData) rowsDataMeta.refetch();
        }}
      />
      <FiltersBar>
        {optionsTypedef?.filterBarContent}
        <PageSizeFilter value={limit} onChange={limitSet} />
      </FiltersBar>
      <ReadminObjectsTable
        {...tableProps}
        header={false} // Note: don't pass header to DataTable for Dialog modals, as it already has a header via FormDialog's header prop
        style={{ margin: "0 -10px" }}
        multiSelectable={isMultiSelectable}
        rows={rowsData?.readminObjects}
        rowsCount={rowsData?.readminObjectsCount}
        typename={optionsTypename}
        dataMeta={rowsDataMeta}
        getDefaultAction={(row) => ({
          content: "Select",
          icon: <Check />,
          onClick: async () => {
            await onSubmit?.(row);
            onClose?.();
          },
          ...(selectedReadminObjects.find((o) => o.id === row.id && o.__typename === row.__typename) && {
            content: "Selected",
            disabled: true,
          }),
        })}
      />
    </FormDialog>
  );
}
